import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, BarChart3, List, Plane, LogOut, Users, Moon, Sun, User } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { toast } from 'react-toastify';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { isDarkMode, toggleDarkMode } = useAdminTheme();

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      toast.success('Logged out successfully');
      // Navigate to login page after successful logout
      navigate('/login', { replace: true });
    } catch (error) {
      toast.error('Failed to logout');
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Filter navigation based on user role
  const getNavigation = () => {
    if (user?.role === 'finance_operator') {
      // Finance operators only see their dashboard
      return [];
    }
    
    // Admin users see all navigation items
    return [
      { name: 'Dashboard', href: '/admin/dashboard', icon: BarChart3 },
      { name: 'Services', href: '/admin/services', icon: Plane },
      { name: 'Payments', href: '/admin/payments', icon: List },
      { name: 'Greeters', href: '/admin/greeters', icon: Users },
    ];
  };

  const navigation = getNavigation();

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Get profile link based on user role
  const getProfileLink = () => {
    if (user?.role === 'finance_operator') {
      return '/finance/profile'; // Finance operators don't have a profile route yet, but this is for future
    }
    return '/admin/profile';
  };

  return (
    <header
      className={`sticky top-0 z-40 shadow-sm border-b transition-colors duration-300 ${
        isDarkMode
          ? 'bg-skyone-surface-dark border-skyone-border-dark/80'
          : 'bg-skyone-surface border-gray-200'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src="/skyone-logo-no-bg.png"
                alt="SkyOne"
                className="h-11 w-auto max-w-[200px] object-contain object-left transition-opacity duration-300 md:h-12"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <nav className="flex space-x-8">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                      isActive(item.href)
                        ? isDarkMode
                          ? 'bg-white/10 text-accent-400'
                          : 'text-primary-700 bg-primary-50'
                        : isDarkMode
                          ? 'text-gray-300 hover:text-white hover:bg-white/10'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
            
            {/* Profile, Dark Mode Toggle and Logout */}
            <div className={`flex items-center space-x-4 pl-4 border-l transition-colors duration-300 ${
              isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
            }`}>
              {/* Profile Button */}
              <Link
                to={getProfileLink()}
                className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive(getProfileLink())
                    ? isDarkMode
                      ? 'text-accent-400 bg-white/10'
                      : 'text-primary-700 bg-primary-50'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-white/10'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </Link>
              
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                  isDarkMode
                    ? 'bg-white/10 text-white hover:bg-white/15 border border-white/15'
                    : 'bg-primary-50 text-primary-900 hover:bg-primary-100 border border-primary-200/80'
                }`}
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? (
                  <>
                    <Sun className="w-4 h-4 mr-2" />
                    Light
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4 mr-2" />
                    Dark
                  </>
                )}
              </button>
              
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className={`flex items-center px-3 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? 'text-gray-200 bg-white/5 border-white/15 hover:bg-white/10'
                    : 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                {isLoggingOut ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Logging out...
                  </>
                ) : (
                  <>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`inline-flex items-center justify-center p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-colors duration-300 ${
                isDarkMode
                  ? 'text-gray-300 hover:text-white hover:bg-white/10'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div
            className={`px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-surface-dark border-skyone-border-dark/80' : 'bg-skyone-surface border-gray-200'
            }`}
          >
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${
                    isActive(item.href)
                      ? isDarkMode
                        ? 'bg-white/10 text-accent-400'
                        : 'text-primary-700 bg-primary-50'
                      : isDarkMode
                        ? 'text-gray-300 hover:text-white hover:bg-white/10'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="flex items-center">
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </div>
                </Link>
              );
            })}
            
            {/* Mobile User Info and Logout */}
            <div className={`border-t pt-3 mt-3 transition-colors duration-300 ${
              isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
            }`}>
              <div className="px-3 py-2">
                <p className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {user?.firstName} {user?.lastName}
                </p>
                <p className={`text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>{user?.email}</p>
              </div>
              
              {/* Mobile Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`w-full flex items-center px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 mb-2 ${
                  isDarkMode
                    ? 'text-white bg-white/10 hover:bg-white/15'
                    : 'text-primary-900 bg-primary-50 hover:bg-primary-100'
                }`}
              >
                {isDarkMode ? (
                  <>
                    <Sun className="w-5 h-5 mr-3" />
                    Light Mode
                  </>
                ) : (
                  <>
                    <Moon className="w-5 h-5 mr-3" />
                    Dark Mode
                  </>
                )}
              </button>
              
              <button
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }}
                disabled={isLoggingOut}
                className={`w-full flex items-center px-3 py-2 text-base font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDarkMode
                    ? 'text-gray-200 hover:text-white hover:bg-white/10'
                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {isLoggingOut ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600 mr-3"></div>
                    Logging out...
                  </>
                ) : (
                  <>
                    <LogOut className="w-5 h-5 mr-3" />
                    Logout
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
